import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Chip, LinearProgress } from "@mui/material";
import { GridColDef, useGridApiRef } from "@mui/x-data-grid-premium";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";

import { UserRoleData } from "@/@types/growthos-api/schema/openapi";
import { CardLoader, GetIconByName } from "@/Components";
import { PrimaryButton } from "@/Components/v2/atoms/Button";
import { Stack } from "@/Components/v2/atoms/Stack";
import Typography from "@/Components/v2/atoms/Typography/Typography";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import CatchErrorCard from "@/shared/components/CatchErrorCard/CatchErrorCard";
import DataGridContainer from "@/shared/components/DataGridContainer";

import AddUserModal from "../AddUser/AddUserModal";
import { useDeleteUser, useDeleteUserInvitation } from "../api/deleteUser";
import { useGetAllUsers } from "../api/getAllUsers";
import EditUserModal from "../EditUser/EditUserModal";

const TeamManagementDashboard = () => {
  const { theme } = useInfo();
  const { data: allUser, isLoading, error } = useGetAllUsers();
  const deleteUserMutation = useDeleteUser();
  const deleteUserInvitationMutation = useDeleteUserInvitation();
  const apiRef = useGridApiRef();

  // modal
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [editedUserData, setEditUserData] = useState<{
    isEditUserOpen: boolean;
    data: UserRoleData | null;
  }>({
    isEditUserOpen: false,
    data: null,
  });

  const handleAddUserModal = () => {
    setIsAddUserOpen(!isAddUserOpen);
  };

  const handleEditUserModal = ({
    data,
    newIsEditUserOpen,
  }: {
    data: UserRoleData | null;
    newIsEditUserOpen: boolean;
  }) => {
    setEditUserData({
      isEditUserOpen: newIsEditUserOpen,
      data,
    });
  };

  const columns: GridColDef<UserRoleData>[] = [
    {
      field: "user",
      headerName: "User",
      width: 300,
      renderCell: (params) => {
        const name = params.row.name;
        return (
          <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
            <div>
              <Typography>{typeof name === "string" ? name : "(not set)"}</Typography>
              <Typography variant="caption">{params.row.email}</Typography>
            </div>
            <GetIconByName
              name="EditIcon"
              onClick={() =>
                handleEditUserModal({
                  data: params.row,
                  newIsEditUserOpen: true,
                })
              }
            />
          </Stack>
        );
      },
    },
    {
      field: "roleName",
      headerName: "Role",
      width: 150,
      renderCell: (params) => {
        // Assign a color to each role type for the Chip
        const roleColors: Record<string, string> = {
          Admin: "#1976d2", // Blue
          Analyst: "#388e3c", // Green
          Viewer: "#fbc02d", // Yellow
        };
        const role = params.row.growthosRoleName;
        return (
          <Chip
            label={role || "No Role"}
            sx={{
              backgroundColor: roleColors[role || ""] || "#e0e0e0",
              color: "#fff",
              fontWeight: 500,
            }}
            size="small"
          />
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => {
        // For the new API, derive status from lastActive
        const hasAccess = !params.row.growthosInvitationId;
        const status = hasAccess ? "Has Access" : "Pending Invite";
        const color = status === "Has Access" ? theme.color : "#adadad";
        return <Typography sx={{ color }}>{status}</Typography>;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <DeleteIcon
            sx={{ cursor: "pointer", color: "#d32f2f" }}
            onClick={() => {
              // Confirm before deleting
              if (
                window.confirm(
                  `Are you sure you want to remove ${params.row.email || "this user"} from the team?`
                )
              ) {
                if (params.row.growthosInvitationId) {
                  deleteUserInvitationMutation.mutate(
                    { cmsUserId: params.row.id, invitationId: params.row.growthosInvitationId },
                    {
                      onSuccess: () => {
                        toast.success("User deleted successfully");
                      },
                    }
                  );
                } else {
                  deleteUserMutation.mutate(params.row.id, {
                    onSuccess: () => {
                      toast.success("User deleted successfully");
                    },
                  });
                }
              }
            }}
          />
        </Stack>
      ),
    },
  ];

  // Transform data for DataGrid
  const rows = useMemo(() => {
    if (!allUser?.users) return [];
    return allUser.users.map((user) => ({
      ...user,
    }));
  }, [allUser?.users]);

  // Return error component if there are errors
  if (error) {
    return <CatchErrorCard title={"Team management"} />;
  }

  if (isLoading || !allUser?.users) {
    return <CardLoader />;
  }

  return (
    <Stack sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" sx={{ fontSize: 19, fontWeight: 500 }}>
          Team Management
        </Typography>
        <PrimaryButton onClick={handleAddUserModal} startIcon={<AddIcon />}>
          Add New User
        </PrimaryButton>
      </Stack>

      <DataGridContainer
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        options={{
          rowHeight: 52,
          sortModel: [{ field: "user", sort: "asc" }],
        }}
        muiDataGridProps={{
          loading: isLoading,
          slots: {
            loadingOverlay: () => <LinearProgress />,
          },
          sx: {
            minHeight: isLoading ? "400px" : "auto",
            "& .MuiDataGrid-overlayWrapper": {
              height: "auto !important",
            },
            "& .MuiDataGrid-overlayWrapperInner": {
              height: "auto !important",
            },
          },
        }}
      />

      <AddUserModal isAddUserOpen={isAddUserOpen} handleAddUserModal={handleAddUserModal} />
      <EditUserModal editedUserData={editedUserData} handleEditUserModal={handleEditUserModal} />
    </Stack>
  );
};

export default TeamManagementDashboard;
